import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Toast } from "primereact/toast";
import PropTypes from "prop-types";
import React, { useRef, forwardRef, useState } from "react";
import classes from "./DataTable.module.css";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Toolbar } from "primereact/toolbar";
import clsx from "clsx";

const maxPossibleRows = 100000;

const DataTableWrapper = forwardRef(
  (
    {
      title,
      data,
      columns,
      onView,
      onAdd,
      onEdit,
      onDelete,
      onDeleteMultiple,
      loading = false,
      customToolbarButtons,
      search = "",
      totalRecords,
      onPageChange,
      first,
      rows = 10,
      showPaginator = true,
      onSort,
      sortField,
      sortOrder,
      selection,
      onSelectionChange,
      exportable = false,
      onExport,
    },
    ref
  ) => {
    const toast = useRef(null);
    const dt = useRef(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageInputTooltip, setPageInputTooltip] = useState("Press 'Enter' key to go to this page.");

    const handleSort = (e) => {
      if (typeof onSort === "function") {
        onSort(e);
      }
    };

    const handlePageChange = (e) => {
      setCurrentPage(e.page + 1);
      if (typeof onPageChange === "function") {
        onPageChange(e);
      }
    };

    const onPageInputChange = (event) => {
      setCurrentPage(event.target.value);
    };

    const onPageInputKeyDown = (event, options) => {
      if (event.key === "Enter") {
        const page = parseInt(currentPage);
        
        if (page < 0 || page > options.totalPages) {
          setPageInputTooltip(`Value must be between 1 and ${options.totalPages}.`);
        } else {
          const first = currentPage ? options.rows * (page - 1) : 0;
          handlePageChange({ ...options, first, page: page - 1 });
          setPageInputTooltip("Press 'Enter' key to go to this page.");
        }
      }
    };

    const paginatorTemplate = {
      layout: "RowsPerPageDropdown PrevPageLink PageLinks NextPageLink CurrentPageReport",
      RowsPerPageDropdown: (options) => {
        const dropdownOptions = [
          { label: 5, value: 5 },
          { label: 10, value: 10 },
          { label: 25, value: 25 },
          { label: 50, value: 50 },
          { label: "All", value: options.totalRecords },
        ];

        return (
          <Dropdown
            value={options.value}
            options={dropdownOptions}
            onChange={options.onChange}
          />
        );
      },
      CurrentPageReport: (options) => {
        return (
          <span className={clsx("mx-3",classes.inputContainer)} style={{ color: "var(--text-color)", userSelect: "none" }}>
            Go to{" "}
            <div className={classes.inputWrapper}>
            <InputText
              size="2"
              className="ml-1"
              value={currentPage}
              tooltip={pageInputTooltip}
              onKeyDown={(e) => onPageInputKeyDown(e, options)}
              onChange={onPageInputChange}
            />
            </div>
          </span>
        );
      },
    };


    return (
      <div>
        <Toast ref={toast} />
        <div className={classes.wrapper}>
          {title && <h2>{title}</h2>}
          <DataTable
            paginatorClassName="custom-paginator"
            ref={ref}
            value={data}
            dataKey="id"
            paginator={showPaginator}
            rows={rows}
            first={first}
            totalRecords={totalRecords}
            onPage={handlePageChange}
            paginatorTemplate={paginatorTemplate}
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} items"
            globalFilter={search}
            loading={loading}
            lazy={true}
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={handleSort}
            sortMode="single"
            removableSort
            selection={selection}
            onSelectionChange={onSelectionChange}
            selectionMode={onSelectionChange ? "multiple" : null}
          >
            {columns.map((col, i) => (
              <Column
                key={col.field || i}
                field={col.field}
                header={col.header}
                body={col.body}
                sortable={col.sortable}
                sortField={col.sortField}
                style={col.style}
                className={col.className}
                frozen={col.frozen}
                align={col.align}
                exportable={col.exportable}
              />
            ))}
          </DataTable>
        </div>
      </div>
    );
  }
);

DataTableWrapper.propTypes = {
  title: PropTypes.string,
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  onAdd: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onView: PropTypes.func,
  onDeleteMultiple: PropTypes.func,
  loading: PropTypes.bool,
  customToolbarButtons: PropTypes.node,
  totalRecords: PropTypes.number,
  onPageChange: PropTypes.func,
  first: PropTypes.number,
  rows: PropTypes.number,
  showPaginator: PropTypes.bool,
  onSort: PropTypes.func,
  sortField: PropTypes.string,
  sortOrder: PropTypes.number,
  selection: PropTypes.array,
  onSelectionChange: PropTypes.func,
  exportable: PropTypes.bool,
  onExport: PropTypes.func,
};

export default DataTableWrapper;
